import { useState } from 'react';
import { Button, Popconfirm, Space, Tooltip, Typography, message } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';

/**
 * Who may not leave a context or relationship: its last admin.
 *
 * `own` is the caller's row from GET /api/entities/{id}/connected and
 * `connections` the whole list. True when `own` is an accepted admin-role
 * link and no other accepted admin-role link to the group confers admin
 * rights — a person always does; a relationship or context does on a context
 * (its admins administer the context too, see backend permissions.is_admin)
 * but not on a relationship; a thing never does. Mirrors the backend's
 * services.connections.sole_admin_group, which is the rule's home: this copy
 * only lets the page explain the refusal before the request is made.
 */
export function isSoleAdmin(own, connections, groupType) {
    if (!own?.connected_status || own.role !== 'admin') return false;
    const confers = type => (groupType === 'context' ? type !== 'app' : type === 'person');
    return !connections.some(c =>
        c.connection_id !== own.connection_id
        && c.connected_status
        && c.role === 'admin'
        && confers(c.entity.entity_type),
    );
}

/**
 * "Leave" for a member of a context or relationship.
 *
 * Deletes the caller's own membership (DELETE /api/connections/{id}): a
 * person is admin of their own person entity, so the backend lets them
 * remove any link they are one end of. Offered to every accepted member,
 * admin or not — on a relationship every member counts as an admin
 * (backend is_admin), so a "non-admins only" rule would leave no way out of
 * one. The one member who can't leave is the group's last admin: the button
 * is disabled with the reason spelled out, and the backend refuses with a
 * 409 as well, so a stale page gets the same answer.
 *
 * Not for the hidden DM relationship (the page never shows it there) or a
 * federated shadow: dropping the local half of a cross-server membership
 * would tell the remote server nothing.
 */
function LeaveEntityButton({ entity, connection, soleAdmin, onLeft }) {
    const api = useApi();
    const [leaving, setLeaving] = useState(false);

    const handleLeave = async () => {
        setLeaving(true);
        try {
            await api.del(`/api/connections/${connection.connection_id}`);
            message.success(`You left ${entity.name}`);
            await onLeft?.();
        } catch (err) {
            message.error(err.message || `Could not leave ${entity.name}`);
        } finally {
            setLeaving(false);
        }
    };

    if (soleAdmin) {
        return (
            <Space size={8} wrap>
                <Tooltip title="The last admin can't leave">
                    <Button danger disabled icon={<LogoutOutlined />}>Leave</Button>
                </Tooltip>
                <Typography.Text type="secondary">
                    You're the only admin — make another member an admin before leaving.
                </Typography.Text>
            </Space>
        );
    }

    return (
        <Popconfirm
            title={`Leave ${entity.name}?`}
            description="You'll lose access to its messages and calls. You can request to join again later."
            okText="Leave"
            okButtonProps={{ danger: true, loading: leaving }}
            onConfirm={handleLeave}
        >
            <Button danger icon={<LogoutOutlined />} loading={leaving}>Leave</Button>
        </Popconfirm>
    );
}

export default LeaveEntityButton;
